/**
 * Server-side session revocation.
 *
 * With EDGE_VERIFY=on, nginx asks istari whether the user's token is still a live
 * session before serving data (docker/nginx/insync_edge_auth.js). When a session
 * has been ended server-side - logged out elsewhere, deactivated, or revoked by
 * an admin - nginx answers 401 with `X-Insync-Session: session`.
 *
 * Only that exact value means "this user's session is over". A 401 marked
 * `config` (server misconfiguration) or `unavailable` (istari unreachable) must
 * NOT sign the user out: a bad deploy or an istari blip would otherwise log every
 * user out in a loop. Those surface as ordinary errors.
 *
 * No imports, so httpClient, the custom functions and the app can all use it.
 */

/** Shown to the user. Generic: no reason codes or server detail. */
export const SESSION_ENDED_MESSAGE = 'Your InSync session has ended. Please log in again.';

export function isSessionEnded(
  response: { status?: number; headers?: { get(name: string): string | null } } | null | undefined
): boolean {
  try {
    return response?.status === 401 && response.headers?.get('X-Insync-Session') === 'session';
  } catch {
    return false;
  }
}

type Listener = () => void;
const listeners = new Set<Listener>();

/** Subscribe (the taskpane App does, to log out and show the login screen). Returns an unsubscribe. */
export function onSessionEnded(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Called by every request path that receives a session-ended 401. */
export function notifySessionEnded(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      /* a listener must never break the request path */
    }
  });
}
